import React, { useState } from "react";
import { Box, Button, Input, Select, SelectOption, Text } from "@showitinc/showit-wc";
import { _ } from "lodash";

import LanguageConstants from "../../../constants/LanguageConstants";
import DesignUtils from "../../../utils/DesignUtils";
import AppConfig from "../../../config/app";
import EditableValue from "../../../components/base/EditableValue";
import SubscribeButton from "../../../components/SubscribeButton";
import { toUnicode } from "../../../utils/DomainUtils";
import ValidationUtils from "../../../utils/ValidationUtils";
import LanguageOptions from "../../../components/sitesettings/LanguageOptions";
import { formatPropUpdate } from "../../../utils/PropertyUtils";
import styles from "./SiteSettingsModal.module.css";
import FaviconChooser from "./FaviconChooser";
import SiteSettingsActions from "../../../actions/SiteSettingsActions";
import { VIEW_ADD_SUBDOMAIN, VIEW_DOMAIN, VIEW_VANITY } from "../../../stores/SiteSettingsStore";
import DomainView from "./DomainView";
import VanityView from "./VanityView";
import AddSubDomainView from "./AddSubDomainView";
import useStoreState from "../../../components/providers/useStoreState";
import { useSiteState } from "../../../hooks/query/useSite";

const SiteInfo = ({ siteSettings, user, onChange, showChangesDialog }) => {
  const [isValid, setIsValid] = useState(true);
  const site = useSiteState();
  const [siteName, setSiteName] = useState(site.get("name", ""));

  const siteLanguage = siteSettings.getIn(
    ["config", "language"],
    LanguageConstants.DEFAULT_LANGUAGE_CODE
  );
  const subdomain = siteSettings.getIn(["site", "subdomain"], false);
  const customDomain = siteSettings.getIn(["site", "domain"], false);
  const favicon = siteSettings.getIn(["config", "favicon"], false);
  const isSubscribed = siteSettings.get("isSubscribed");
  const currentView = siteSettings.get("view");

  const isActive = user.get("isActive");

  const _updateCustomDomain = () => {
    SiteSettingsActions.changeView(VIEW_DOMAIN);
  };
  const _onFaviconUpdate = (/** Asset * */ newFavicon) => {
    const propInfo = {
      name: "config.favicon",
    };

    if (newFavicon) {
      propInfo.value = newFavicon;
    } else {
      propInfo.value = null;
    }

    onChange(propInfo);
  };

  const _onSiteNameChange = (e) => {
    setSiteName(e.target.value);
    onChange(formatPropUpdate("site.name", e.target.value));
  };

  const _validateString = (data) => {
    const result = {
      valid: true,
      message: "",
    };

    if (ValidationUtils.isEmptyStr(data.value)) {
      result.valid = false;
      result.message = `${data.label} cannot be empty`;
    }

    return result;
  };

  const _editVanity = () => {
    SiteSettingsActions.changeView(VIEW_VANITY);
  };

  const subDomainName = _.isEmpty(subdomain)
    ? DesignUtils.encodeKeyForSubdomain(siteSettings.getIn(["site", "key"]))
    : subdomain;

  const siteDomain = `${subDomainName}.${AppConfig.siteDomain}`;
  let publishDomain = _.isEmpty(customDomain) ? siteDomain : customDomain;

  const labelStyle = {
    width: "21%",
  };

  const { protocol } = window.location;
  let publishDomainProtocol = protocol;

  let customDomainField = null;
  if (customDomain) {
    const wwwRequired = siteSettings.getIn(["site", "domain_info", "www_required"], false);
    const sslRequired = siteSettings.getIn(["site", "domain_info", "ssl_enabled"], false);

    publishDomain = (wwwRequired ? "www." : "") + publishDomain;
    publishDomainProtocol = sslRequired ? "https:" : "http:";

    customDomainField = (
      <EditableValue
        value={toUnicode(customDomain || "")}
        btnText="Edit"
        onClick={_updateCustomDomain}
      />
    );
  } else {
    if (isSubscribed) {
      customDomainField = (
        <EditableValue value="Domain not set" btnText="Edit" onClick={_updateCustomDomain} />
      );
    } else {
      customDomainField = <SubscribeButton>Subscribe Now</SubscribeButton>;
    }
  }
  if (currentView === VIEW_DOMAIN) {
    return (
      <DomainView
        isSubscribed={isSubscribed}
        showChangesDialog={showChangesDialog}
        siteSettings={siteSettings}
        onChange={onChange}
      />
    );
  }
  if (currentView === VIEW_VANITY) {
    return (
      <VanityView
        isValid={isValid}
        setIsValid={setIsValid}
        siteSettings={siteSettings}
        onChange={onChange}
        appConfig={AppConfig}
      />
    );
  }

  if (currentView === VIEW_ADD_SUBDOMAIN) {
    return <AddSubDomainView siteSettings={siteSettings} onChange={onChange} />;
  }

  return (
    <div className={styles.siteInfo} data-testid="site-info">
      <Box px="3" pb="1" pt="2" d="flex" align="center">
        <Text variant="body-base" style={labelStyle}>
          Favicon
        </Text>
        <FaviconChooser favicon={favicon} onUpdate={_onFaviconUpdate} />
      </Box>
      {/* Todo add validation */}
      <Input
        className={styles.fullInput}
        data-testid="site-name-input"
        label="Site Name"
        fullWidth
        value={siteName}
        onSIChange={_onSiteNameChange}
        onSIInput={_onSiteNameChange}
      />
      <Select
        className={styles.fullSelect}
        label="Site Language"
        fullWidth
        value={siteLanguage}
        helpTooltip="site-language"
        onSISelect={(e) =>
          onChange(formatPropUpdate("config.language", e.target.value, siteLanguage))
        }
        minParent
        filter
      >
        <SelectOption value="">-- Select a Language --</SelectOption>
        <LanguageOptions />
      </Select>
      {isSubscribed ? (
        <Input
          className={styles.complexInput}
          label="Custom Domain"
          fullWidth
          placeholder="Domain not set"
          value={toUnicode(customDomain || "")}
          onSIFocus={_updateCustomDomain}
        >
          <Button
            className={styles.editButton}
            onClick={_updateCustomDomain}
            slot="suffix"
            iconPrefix="edit"
            variant="icon"
          />
        </Input>
      ) : (
        <Box px="3" py="3" d="flex" align="center">
          <Text variant="body-base" style={labelStyle}>
            Custom Domain
          </Text>
          <Box ml="4">
            <SubscribeButton>Subscribe Now</SubscribeButton>
          </Box>
        </Box>
      )}
      <Input
        className={styles.complexInput}
        label="Vanity URL Slug"
        value={subdomain}
        fullWidth
        onSIFocus={_editVanity}
      >
        <Button
          className={styles.editButton}
          slot="suffix"
          iconPrefix="edit"
          variant="icon"
          onClick={_editVanity}
        />
      </Input>
    </div>
  );
};

export default SiteInfo;
